import React, { useEffect, useMemo, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Button } from 'components/ui';
import { textSizeByBreakpoint } from 'utils/helper';

type Props = {
  open: boolean;
  startDate?: Date;
  endDate?: Date;
  onClose: () => void;
  onApply: (startDate: Date, endDate: Date) => void;
};

const DEFAULT_RANGE_DAYS = 7;

const CALENDARS = [
  { key: 'start' as const, label: 'Start' },
  { key: 'end' as const, label: 'End' }
];

type Draft = { start: Date | null; end: Date | null };

/** Picking a start after the current end drags the end along, so the range can never invert. */
const nextDraft = (draft: Draft, key: 'start' | 'end', value: Date | null): Draft => {
  const next = { ...draft, [key]: value };
  if (key === 'start' && value && next.end && next.end < value) next.end = value;
  return next;
};

const DateRangePickerDialog = ({ open, startDate, endDate, onClose, onApply }: Props) => {
  const theme = useTheme();
  const [draft, setDraft] = useState<Draft>({ start: null, end: null });

  useEffect(() => {
    if (!open) return;

    if (startDate && endDate) {
      setDraft({ start: startDate, end: endDate });
      return;
    }

    const now = new Date();
    setDraft({ start: new Date(now.getTime() - DEFAULT_RANGE_DAYS * 24 * 60 * 60 * 1000), end: now });
  }, [open, startDate, endDate]);

  // minDate already stops the calendar offering an inverted range; this catches one arriving as a prop.
  const errorText = useMemo(() => {
    if (!draft.start || !draft.end) return '';
    if (draft.end.getTime() < draft.start.getTime()) return 'End date must be after start date';
    return '';
  }, [draft]);

  const canApply = Boolean(draft.start && draft.end && !errorText);

  const handleApply = () => {
    if (!draft.start || !draft.end) return;
    onApply(draft.start, draft.end);
  };

  const calendarSx = {
    '& .MuiPickersDay-root.Mui-selected': {
      backgroundColor: theme.palette.primary.main
    },
    '& .MuiPickersDay-root.Mui-selected:hover': {
      backgroundColor: theme.palette.primary.main,
      opacity: 0.9
    },
    '& .MuiPickersDay-root.Mui-selected:focus': {
      backgroundColor: theme.palette.primary.main
    },
    '& .MuiPickersDay-today:not(.Mui-selected)': {
      borderColor: theme.palette.primary.main
    }
  } as const;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: { xs: '100%', sm: 860 },
          borderRadius: '14px',
          border: '1px solid',
          borderColor: theme.palette.border.main,
          backgroundColor: theme.palette.background.default
        }
      }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Typography fontSize={textSizeByBreakpoint(18)} fontWeight={700}>
          Custom Range
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              divider={<Divider flexItem orientation="vertical" sx={{ borderColor: theme.palette.border.main, opacity: 1 }} />}
              sx={{
                '& .MuiPickersToolbar-root': { display: 'none' },
                '& .MuiPickersStaticWrapper-root': {
                  backgroundColor: theme.palette.background.default
                }
              }}
            >
              {CALENDARS.map(({ key, label }) => (
                <Box key={key} sx={{ flex: 1 }}>
                  <Typography fontSize={textSizeByBreakpoint(14)} fontWeight={600} sx={{ mb: 1, color: theme.palette.text.secondary }}>
                    {label}
                  </Typography>
                  <Box sx={calendarSx}>
                    <StaticDatePicker
                      displayStaticWrapperAs="desktop"
                      value={draft[key]}
                      onChange={(newValue) => setDraft(nextDraft(draft, key, newValue))}
                      // End cannot go before start, so an inverted range is never selectable.
                      minDate={key === 'end' ? draft.start ?? undefined : undefined}
                      renderInput={() => null as any}
                      componentsProps={{
                        actionBar: { actions: [] }
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>

            {errorText ? (
              <Typography fontSize={12} color={theme.palette.error.main}>
                {errorText}
              </Typography>
            ) : (
              <Box />
            )}

            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="primary"
                onClick={onClose}
                sx={{ borderRadius: '10px', borderColor: theme.palette.border.main, color: theme.palette.text.primary }}
              >
                Cancel
              </Button>
              <Button variant="contained" color="primary" onClick={handleApply} disabled={!canApply} sx={{ borderRadius: '10px' }}>
                Apply
              </Button>
            </Stack>
          </Stack>
        </LocalizationProvider>
      </DialogContent>
    </Dialog>
  );
};

export default DateRangePickerDialog;
